/**
 * Public Academy enrollment request — `/academy/enroll`.
 *
 * STRICT RULES (Feb 2026):
 *   - NO pricing, NO payment links, NO direct checkout on this page.
 *   - Submits to POST /api/academy/enrollment-requests which stores a row
 *     for admin review. A ZHELTA admin then sends a private Stripe payment
 *     link; the student account is provisioned ONLY after the webhook fires.
 *
 * Copy is curated / selective — positions enrollment as guided, not transactional.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, Lock } from 'lucide-react';
import AcademyHeader from '../components/AcademyHeader';
import AcademyFooter from '../components/AcademyFooter';

const API = (process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL);

const PATHS = [
  {
    id: 'standard',
    name: 'Standard',
    sub: 'Self-paced learning with structured guidance and mentor support.',
  },
  {
    id: 'premium',
    name: 'Premium',
    sub: 'Cohort-based learning with personalized mentoring and applied project work.',
  },
  {
    id: 'career_cohort',
    name: 'Career Cohort',
    sub: 'An immersive, structured program with coaching, real-world preparation, and career support.',
  },
];

const VALID_PATH_IDS = new Set(PATHS.map((p) => p.id));

export default function AcademyEnrollPage() {
  const [params] = useSearchParams();
  // Tier-card deep-links pass ?program=<slug>&path=<learning_path>.
  // When ?path= is valid, we lock the form to ONLY that path (user already
  // picked on the program detail). Top-nav ENROLL NOW omits the param so
  // all 3 paths stay visible.
  const presetProgram = params.get('program') || '';
  const presetPath = params.get('path') || '';
  const pathLocked = VALID_PATH_IDS.has(presetPath);
  const visiblePaths = pathLocked ? PATHS.filter((p) => p.id === presetPath) : PATHS;

  const [programs, setPrograms] = useState([]);
  const [form, setForm] = useState({
    name: '',
    email: '',
    program_slug: presetProgram,
    learning_path: pathLocked ? presetPath : 'standard',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/api/academy/programs`)
      .then(({ data }) => {
        // Public program catalog — names only, NO pricing shown anywhere.
        const rows = Array.isArray(data?.programs)
          ? data.programs
          : Array.isArray(data?.rows)
          ? data.rows
          : Array.isArray(data)
          ? data
          : [];
        setPrograms(rows);
        if (rows.length) {
          setForm((f) => (f.program_slug ? f : { ...f, program_slug: rows[0].slug }));
        }
      })
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await axios.post(`${API}/api/academy/enrollment-requests`, form);
      setDone(true);
    } catch (e2) {
      setErr(e2.response?.data?.detail || e2.message);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-white flex flex-col" data-testid="enroll-success">
        <AcademyHeader />
        <main className="flex-1 flex items-center justify-center px-4 py-16">
          <div className="max-w-md text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
            <h1 className="text-2xl font-semibold text-zinc-900 mt-5 tracking-tight">
              Request received
            </h1>
            <p className="text-zinc-600 mt-3 leading-relaxed text-[14px]">
              Thank you{form.name ? `, ${form.name.split(' ')[0]}` : ''}. A member of the ZHELTA
              Academy team will review your request and follow up at{' '}
              <strong className="text-zinc-900">{form.email}</strong> with your enrollment pathway.
            </p>
            <p className="text-zinc-400 mt-4 text-[11.5px] italic leading-relaxed">
              Enrollment is reviewed and processed individually. Access and onboarding are provided
              after confirmation.
            </p>
            <Link
              to="/academy"
              data-testid="back-to-academy"
              className="inline-block mt-8 px-5 py-2.5 rounded-md bg-[#c9a227] text-[#18181b] font-semibold text-[13px] hover:brightness-110 transition"
            >
              Back to Academy
            </Link>
          </div>
        </main>
        <AcademyFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="academy-enroll-page">
      <AcademyHeader />
      <main className="flex-1 px-4 py-12 sm:py-16">
        <div className="mx-auto max-w-2xl">
          {/* Eyebrow + Heading */}
          <div className="text-center mb-10">
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#c9a227]">
              Ready to enroll
            </div>
            <h1 className="mt-3 text-3xl sm:text-4xl font-semibold text-zinc-900 tracking-tight">
              Choose how you want to learn.
            </h1>
            <p className="mt-4 text-[14px] text-zinc-600 leading-relaxed max-w-lg mx-auto">
              Select your program and learning path. Our team will guide you through the next steps.
            </p>
          </div>

          {/* Form card */}
          <form
            onSubmit={submit}
            className="bg-white rounded-2xl border border-zinc-200/70 shadow-[0_1px_2px_rgba(0,0,0,0.04)] overflow-hidden"
          >
            <div className="px-6 sm:px-8 py-6 sm:py-8 space-y-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Full name">
                  <input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className={inp}
                    data-testid="enroll-name"
                    autoComplete="name"
                  />
                </Field>
                <Field label="Email">
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className={inp}
                    data-testid="enroll-email"
                    autoComplete="email"
                  />
                </Field>
              </div>

              <Field label="Program">
                <select
                  required
                  value={form.program_slug}
                  onChange={(e) => setForm({ ...form, program_slug: e.target.value })}
                  className={inp}
                  data-testid="enroll-program"
                >
                  <option value="" disabled>
                    Choose a program
                  </option>
                  {programs.map((p) => (
                    <option key={p.slug} value={p.slug}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </Field>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                    Learning Path
                  </label>
                  {pathLocked && (
                    <Link
                      to={presetProgram ? `/academy/enroll?program=${encodeURIComponent(presetProgram)}` : '/academy/enroll'}
                      data-testid="enroll-path-change"
                      className="text-[11px] font-semibold text-[#c9a227] hover:brightness-90 transition"
                    >
                      Change path
                    </Link>
                  )}
                </div>
                <div className="grid gap-2">
                  {visiblePaths.map((p) => {
                    const active = form.learning_path === p.id;
                    return (
                      <label
                        key={p.id}
                        data-testid={`enroll-path-${p.id}`}
                        className={`flex items-start gap-3 border rounded-lg px-4 py-3 transition-colors duration-150 ${
                          pathLocked
                            ? 'border-[#c9a227] bg-amber-50/40 ring-1 ring-[#c9a227]/20 cursor-default'
                            : active
                            ? 'border-[#c9a227] bg-amber-50/40 ring-1 ring-[#c9a227]/20 cursor-pointer'
                            : 'border-zinc-200 hover:bg-zinc-50 cursor-pointer'
                        }`}
                      >
                        {pathLocked ? (
                          <Lock className="w-4 h-4 mt-0.5 text-[#c9a227]" />
                        ) : (
                          <input
                            type="radio"
                            name="learning_path"
                            value={p.id}
                            checked={active}
                            onChange={() => setForm({ ...form, learning_path: p.id })}
                            className="mt-1 accent-[#c9a227]"
                          />
                        )}
                        <div>
                          <div className="font-semibold text-[13.5px] text-zinc-900">{p.name}</div>
                          <div className="text-[12.5px] text-zinc-500 leading-relaxed mt-0.5">
                            {p.sub}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {err && (
                <div
                  className="text-[12.5px] text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                  data-testid="enroll-error"
                >
                  {err}
                </div>
              )}
            </div>

            <div className="px-6 sm:px-8 py-5 bg-white border-t border-zinc-100">
              <button
                type="submit"
                disabled={busy}
                data-testid="enroll-submit"
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md text-[13.5px] font-semibold text-zinc-900 hover:brightness-95 disabled:opacity-60 transition-all"
                style={{
                  background: 'linear-gradient(90deg, #ffb347 0%, #f5a623 50%, #ff8c1a 100%)',
                  boxShadow: '0 10px 20px -8px rgba(245, 140, 25, 0.45)',
                }}
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                {busy ? 'Submitting…' : 'Request Enrollment'}
              </button>
              <p className="mt-3 text-[11.5px] text-zinc-400 text-center leading-relaxed">
                Submit your details to begin. A member of the ZHELTA Academy team will follow up
                with your enrollment pathway.
              </p>
            </div>
          </form>

          {/* Footer note */}
          <p className="mt-6 text-center text-[11.5px] text-zinc-400 italic leading-relaxed">
            Enrollment is reviewed and processed individually. Access and onboarding are provided
            after confirmation.
          </p>

          {/* Optional secondary CTA */}
          <div
            className="mt-10 text-center border-t border-zinc-100 pt-8"
            data-testid="enroll-secondary-cta"
          >
            <div className="text-[13px] text-zinc-700 font-medium">Not sure where to start?</div>
            <Link
              to="/academy/programs"
              className="inline-block mt-2 text-[12.5px] font-semibold text-[#c9a227] hover:brightness-90 transition"
              data-testid="enroll-secondary-cta-link"
            >
              Get a recommendation tailored to your goals →
            </Link>
          </div>
        </div>
      </main>
      <AcademyFooter />
    </div>
  );
}

const inp =
  'w-full px-3 py-2.5 bg-white border border-zinc-200 rounded-md text-[13.5px] text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900/5 focus:border-zinc-400 transition';

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-1.5">
        {label}
      </label>
      {children}
    </div>
  );
}
